/**
 * The frame every screen renders inside (spec M0-F4 §7.2).
 *
 * ```
 * ┌──────────────────────────── top bar 48 ────────────────────────────┐
 * ├────┬───────────────┬───────────────────────────────────────────────┤
 * │rail│ pane 279      │ <Outlet/>                    [SidePanel 700]  │
 * │ 72 │ (resizable)   │ main, min-width 0, own scroll                 │
 * └────┴───────────────┴───────────────────────────────────────────────┘
 * ```
 * The main region owns the scroll; the shell never scrolls. The three overlay hosts come from
 * `UiProvider` in `main.tsx`, so no screen invents its own.
 */
import { AttentionBox, PageErrorState, breakpoints } from '@elizmonday/ui';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { CAPABILITIES_WHILE_LOADING, useBootstrap } from '../modules/shell/bootstrap';
import { useAccountBranding } from '../modules/shell/branding';
import { isThemeName, usePreference, usePreferencesHydrated } from '../modules/shell/preferences';
import { useShellWorkspaceId } from '../modules/shell/current-workspace';
import { useShortcut } from '../modules/shell/shortcuts';
import { useShellStore } from '../modules/shell/store';
import type { ProfileMenuUser } from './ProfileMenu';
import { Sidebar } from './sidebar/Sidebar';
import { ImpersonationBanner } from '../modules/admin/impersonation';
import { CardToastHost } from '../modules/item-card/CardToast';
import { ShellOverlays } from './overlays/ShellOverlays';
import { TopBar } from './TopBar';
import { useUnreadCounts } from './unread';

/**
 * §7.10 / M0-F9 §3. monday serves an app interstitial on a phone, so there is nothing to copy:
 * the numbers come from `packages/ui`'s breakpoint registry and are used here and in
 * `mobile.css` from the same source, which is what stops the two drifting.
 */
const PANE_OVERLAY_BELOW = breakpoints.tablet + 1;
const OFF_CANVAS_BELOW = breakpoints.phone + 1;

function useViewportMode() {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    paneAsOverlay: width < PANE_OVERLAY_BELOW,
    offCanvas: width < OFF_CANVAS_BELOW,
  };
}

export function AppShell() {
  const bootstrap = useBootstrap();
  /**
   * M8-P2 §6.12: the account's brand colours become CSS custom properties on `:root`, so an
   * admin's change repaints the top bar and every primary button with no reload.
   */
  useAccountBranding(bootstrap.data?.account ?? null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { paneAsOverlay, offCanvas } = useViewportMode();

  const [expanded, setExpanded] = usePreference('shell.sidebarExpanded');
  const [lastWorkspaceId, setLastWorkspaceId] = usePreference('shell.lastWorkspaceId');
  /** P-170 / T-9h: nothing may be written back before the server's copy has been read. */
  const preferencesHydrated = usePreferencesHydrated();

  const resizing = useShellStore((state) => state.resizing);
  const reconnecting = useShellStore((state) => state.reconnecting);
  const paneOverlayOpen = useShellStore((state) => state.paneOverlayOpen);
  const setPaneOverlayOpen = useShellStore((state) => state.setPaneOverlayOpen);
  const toggleOverlay = useShellStore((state) => state.toggleOverlay);
  const openOverlay = useShellStore((state) => state.openOverlay);
  const closeOverlay = useShellStore((state) => state.closeOverlay);
  const overlay = useShellStore((state) => state.overlay);

  const capabilities = bootstrap.data?.capabilities ?? CAPABILITIES_WHILE_LOADING;
  /**
   * **P-168** — the badge is live. It used to be the one-shot `shell.bootstrap` snapshot, which
   * nothing invalidated, so `Mark all as read` left the bell's red counter behind.
   */
  const unread = useUnreadCounts(bootstrap.data?.unread ?? { notifications: 0, inbox: 0 });
  /**
   * The server stores three themes but phase 1 renders only `light` (F7 E7): an unknown or
   * not-yet-supported value is coerced here rather than anywhere near the render tree.
   */
  const me: ProfileMenuUser | null = bootstrap.data
    ? {
        id: bootstrap.data.me.id,
        name: bootstrap.data.me.name,
        email: bootstrap.data.me.email,
        photoUrl: bootstrap.data.me.photoUrl,
        theme: isThemeName(bootstrap.data.me.theme) ? bootstrap.data.me.theme : 'light',
      }
    : null;
  /** P-026: on a board the shell follows the **board's** workspace (`modules/shell`). */
  const workspaceId = useShellWorkspaceId({
    routeWorkspaceId: params['workspaceId'] ?? null,
    boardId: params['boardId'] ?? null,
    lastWorkspaceId: lastWorkspaceId ?? null,
    /*
     * T-9h: a cold start lands on the account's **default** workspace ("Main workspace"),
     * not on whichever workspace sorts first — the user's own last choice above still wins.
     */
    fallbackWorkspaceId:
      bootstrap.data?.workspaces.defaultId ?? bootstrap.data?.workspaces.recent[0]?.id ?? null,
  });
  const selectedEntityId = params['boardId'] ?? params['docId'] ?? params['dashboardId'] ?? null;

  /*
   * E1/§7.5: the workspace the shell is looking at is remembered, so `/` can resolve next time.
   *
   * **P-170, and the half T-9h caught**: not before `shell.bootstrap` has hydrated the store.
   * `workspaceId` resolves from the board or from the *fallback* long before the server's own
   * `shell.lastWorkspaceId` has arrived, so an ungated write sent the account's default
   * workspace to the server on every cold start and erased the user's actual choice — the
   * preference survived a reload and still lost, because it was overwritten a frame later.
   */
  useEffect(() => {
    if (!preferencesHydrated) return;
    if (workspaceId && workspaceId !== lastWorkspaceId) setLastWorkspaceId(workspaceId);
  }, [preferencesHydrated, workspaceId, lastWorkspaceId, setLastWorkspaceId]);

  // Navigating closes the pane overlay, per §7.10.
  useEffect(() => setPaneOverlayOpen(false), [location.pathname, setPaneOverlayOpen]);

  useShortcut(
    'search.open',
    useCallback(() => openOverlay('search'), [openOverlay]),
  );
  useShortcut(
    'shortcuts.open',
    useCallback(() => openOverlay('shortcuts'), [openOverlay]),
  );
  useShortcut(
    'sidebar.toggle',
    useCallback(() => setExpanded(!expanded), [expanded, setExpanded]),
  );
  useShortcut(
    'notifications.toggle',
    useCallback(() => toggleOverlay('notifications'), [toggleOverlay]),
  );
  useShortcut(
    'inbox.open',
    useCallback(() => navigate('/my_inbox'), [navigate]),
  );
  useShortcut(
    'mywork.open',
    // M5.C3: monday moved My Work to `/my_work`; `/my_week` is now only a redirect.
    useCallback(() => navigate('/my_work'), [navigate]),
  );
  useShortcut(
    'nav.back',
    useCallback(() => navigate(-1), [navigate]),
  );
  // P5: Escape closes exactly one layer at a time, topmost first.
  useShortcut(
    'escape',
    useCallback(() => {
      if (overlay) {
        closeOverlay();
        return;
      }
      if (paneOverlayOpen) setPaneOverlayOpen(false);
    }, [overlay, closeOverlay, paneOverlayOpen, setPaneOverlayOpen]),
  );

  // §7.10: the rail is always on screen down to 768 px; only the pane comes and goes.
  const paneVisible = expanded && (!paneAsOverlay || paneOverlayOpen);

  return (
    <div
      className="ez-app"
      data-testid="app-shell"
      data-resizing={resizing || undefined}
      data-offcanvas={offCanvas && paneOverlayOpen ? 'true' : undefined}
    >
      {/*
       * M8-P3 §4 — the "View as <name> · view only" bar, first child of the shell so it pushes
       * the top bar down rather than floating over it, and on its own layer so nothing the
       * product can raise is able to cover it. Renders nothing on an ordinary session.
       */}
      <ImpersonationBanner />

      <TopBar
        user={me}
        accountName={bootstrap.data?.account.name ?? null}
        capabilities={capabilities}
        unread={unread}
        {...(offCanvas ? { onToggleOffCanvas: () => setPaneOverlayOpen(!paneOverlayOpen) } : {})}
      />

      {/* N3: non-blocking, under the top bar. */}
      {reconnecting ? (
        <div className="ez-reconnecting" data-testid="reconnecting-banner">
          <AttentionBox type="warning" text="Reconnecting…" />
        </div>
      ) : null}

      <div className="ez-app__body">
        <Sidebar
          workspaceId={workspaceId}
          selectedEntityId={selectedEntityId}
          capabilities={capabilities}
          paneVisible={paneVisible}
          asOverlay={paneAsOverlay && paneOverlayOpen}
          onRequestPane={() => setPaneOverlayOpen(true)}
          onDismissOverlay={() => setPaneOverlayOpen(false)}
        />

        <main className="ez-app__main" data-testid="app-main">
          {bootstrap.isError ? (
            <PageErrorState
              kind="error"
              title="The shell could not load"
              hint="The sidebar and the top bar keep working; retrying usually fixes the rest."
              action={
                <button type="button" className="ez-profile-menu__item" onClick={() => bootstrap.refetch()}>
                  Retry
                </button>
              }
              data-testid="shell-error"
            />
          ) : (
            <Outlet />
          )}
        </main>

        {/*
          * **P-172 — the pane hangs below the top bar.** `SlideOverPane` is a full-height
          * `position: absolute` surface, and mounted as a child of `.ez-app` its containing
          * block was the viewport: the notifications pane painted *over* the bell that opened
          * it (measured with `elementFromPoint`, which answered `header.ez-slide-over__header`),
          * so the bell's toggle could never be clicked a second time and the search field and
          * the account menu were unreachable while it was up. monday hangs the pane under the
          * 48 px bar `[ui:library/shell-*]`.
          *
          * Mounting it inside `.ez-app__body` — which is `position: relative` and already
          * starts under the top bar **and** under the impersonation banner — is what says that
          * in the layout rather than in a magic offset that a second banner would falsify. The
          * modal overlays (search, invite, shortcuts) are `position: fixed`, so this changes
          * nothing for them.
          */}
        <ShellOverlays />
      </div>

      {/*
        * P-164 — the item card's toast. It is mounted **here**, not in the card, because
        * Archive, Delete and Move to board all close the card: a toast rendered inside the
        * panel was unmounted in the same commit that raised it, so the one surface that could
        * offer Undo showed nothing at all.
        */}
      <CardToastHost />
    </div>
  );
}
